import { logError } from '@/utils/logger';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  PaperPlaneRight, Paperclip, Camera, Microphone, X, Plus,
  MagnifyingGlass, UsersThree, File as FileIcon, Image as ImageIcon,
  VideoCamera, FileDoc, Record, StopCircle, ArrowLeft, DownloadSimple, Megaphone
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ========================================
// ATTACHMENT MENU
// ========================================
const AttachMenu = ({ open, onClose, onSelectFile, onCapture }) => {
  if (!open) return null;
  const items = [
    { icon: ImageIcon, label: 'Photo / Vidéo', color: '#10B981', accept: 'image/*,video/*', action: 'file' },
    { icon: FileDoc, label: 'Document', color: '#2563EB', accept: '.pdf,.doc,.docx,.xls,.xlsx,.txt', action: 'file' },
    { icon: Camera, label: 'Capturer photo', color: '#F59E0B', action: 'capture-photo' },
    { icon: VideoCamera, label: 'Enregistrer vidéo', color: '#8B5CF6', action: 'capture-video' },
  ];

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute bottom-full left-0 mb-2 bg-[#1E293B] border border-[#334155] rounded-lg shadow-xl z-50 p-2 w-52" data-testid="attach-menu">
        {items.map((item) => (
          <label
            key={item.label}
            className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-[#334155] cursor-pointer transition-colors"
            onClick={(e) => {
              if (item.action !== 'file') {
                e.preventDefault();
                onCapture(item.action === 'capture-photo' ? 'photo' : 'video');
                onClose();
              }
            }}
          >
            <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${item.color}20` }}>
              <item.icon size={18} style={{ color: item.color }} weight="fill" />
            </div>
            <span className="text-sm text-[#F8FAFC]">{item.label}</span>
            {item.action === 'file' && (
              <input type="file" className="hidden" multiple accept={item.accept} onChange={(e) => { onSelectFile(e); onClose(); }} />
            )}
          </label>
        ))}
      </div>
    </>
  );
};

// ========================================
// AUDIO RECORDER BAR
// ========================================
const AudioRecorder = ({ onSend, onCancel }) => {
  const [duration, setDuration] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="flex items-center gap-3 w-full px-3 py-2" data-testid="audio-recorder">
      <button onClick={onCancel} className="text-[#EF4444] hover:text-[#FCA5A5] transition-colors" title="Annuler">
        <X size={22} weight="bold" />
      </button>

      <div className="flex-1 flex items-center gap-3 bg-[#1E293B] rounded-full px-4 py-2">
        <Record size={16} className="text-[#EF4444] animate-pulse" weight="fill" />
        <div className="flex-1 h-1 bg-[#334155] rounded-full overflow-hidden">
          <div className="h-full bg-[#EF4444] rounded-full animate-pulse" style={{ width: `${Math.min((duration / 120) * 100, 100)}%` }} />
        </div>
        <span className="text-sm text-[#F8FAFC] font-mono min-w-[40px]">{formatTime(duration)}</span>
      </div>

      <button onClick={onSend} className="h-10 w-10 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] flex items-center justify-center transition-colors" title="Envoyer l'audio" data-testid="send-audio-btn">
        <PaperPlaneRight size={18} className="text-white" />
      </button>
    </div>
  );
};

// ========================================
// ATTACHMENT PREVIEW BAR
// ========================================
const AttachmentPreview = ({ attachments, onRemove }) => {
  if (attachments.length === 0) return null;
  return (
    <div className="px-4 py-2.5 border-t border-[#1E293B] bg-[#0B1120] flex flex-wrap gap-2">
      {attachments.map((att, i) => (
        <div key={att.file_id || i} className="relative group">
          {att.type === 'image' ? (
            <div className="h-16 w-16 rounded-lg overflow-hidden border border-[#334155]">
              <img src={`${API}/api/files/${att.file_id}`} alt="" className="h-full w-full object-cover" />
            </div>
          ) : (
            <div className="h-16 px-3 rounded-lg border border-[#334155] bg-[#1E293B] flex items-center gap-2">
              {att.type === 'video' ? <VideoCamera size={18} className="text-[#8B5CF6]" /> :
               att.type === 'audio' ? <Microphone size={18} className="text-[#F59E0B]" /> :
               <FileIcon size={18} className="text-[#2563EB]" />}
              <span className="text-xs text-[#F8FAFC] max-w-[80px] truncate">{att.filename}</span>
            </div>
          )}
          <button onClick={() => onRemove(i)} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#EF4444] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <X size={10} className="text-white" />
          </button>
        </div>
      ))}
    </div>
  );
};

// ========================================
// MESSAGE BUBBLE
// ========================================
const MessageBubble = ({ msg, isMine }) => {
  const renderAttachment = (att) => {
    const url = `${API}/api/files/${att.file_id}`;
    if (att.type === 'image') {
      return (
        <img src={url} alt={att.filename} className="max-w-[240px] rounded-lg cursor-pointer hover:opacity-90 transition-opacity" onClick={() => window.open(url)} />
      );
    }
    if (att.type === 'video') {
      return <video src={url} controls className="max-w-[280px] rounded-lg" playsInline />;
    }
    if (att.type === 'audio') {
      return (
        <div className="flex items-center gap-2 min-w-[200px]">
          <Microphone size={16} className="text-[#F59E0B] flex-shrink-0" />
          <audio src={url} controls className="w-full h-8" style={{ filter: isMine ? 'invert(1) hue-rotate(180deg)' : 'none' }} />
        </div>
      );
    }
    return (
      <a href={url} target="_blank" rel="noreferrer" className={`flex items-center gap-3 ${isMine ? 'bg-[#1D4ED8]' : 'bg-[#0F172A]'} rounded-lg p-3 hover:opacity-80 transition-opacity`}>
        <FileIcon size={24} className="text-white/80 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white truncate">{att.filename}</p>
          <p className="text-[10px] text-white/50">{att.content_type}</p>
        </div>
        <DownloadSimple size={18} className="text-white/60 flex-shrink-0" />
      </a>
    );
  };

  return (
    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-1`}>
      {!isMine && (
        <div className="h-7 w-7 rounded-full bg-[#334155] flex items-center justify-center text-white text-[10px] font-bold mr-2 mt-1 flex-shrink-0">
          {msg.sender?.name?.charAt(0) || '?'}
        </div>
      )}
      <div className={`max-w-[70%] ${isMine ? 'bg-[#2563EB]' : 'bg-[#1E293B]'} rounded-2xl ${isMine ? 'rounded-tr-sm' : 'rounded-tl-sm'} px-3.5 py-2`}>
        {!isMine && <p className="text-[11px] font-semibold text-[#60A5FA] mb-0.5">{msg.sender?.name}</p>}
        {msg.attachments?.length > 0 && (
          <div className="space-y-1.5 mb-1">{msg.attachments.map((att, i) => <div key={att.file_id || i}>{renderAttachment(att)}</div>)}</div>
        )}
        {msg.text && <p className="text-[13px] text-white leading-relaxed whitespace-pre-wrap break-words">{msg.text}</p>}
        <p className="text-[10px] text-white/40 mt-0.5 text-right">{new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
      </div>
    </div>
  );
};

// ========================================
// MAIN MESSAGES COMPONENT
// ========================================
const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAnnounceOpen, setIsAnnounceOpen] = useState(false);
  const [announceForm, setAnnounceForm] = useState({ title: '', message: '' });
  const [presence, setPresence] = useState([]);

  useEffect(() => {
    const fetchPresence = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/presence`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(setPresence)
        .catch(() => {});
    };
    fetchPresence();
    const interval = setInterval(fetchPresence, 30000);
    return () => clearInterval(interval);
  }, []);

  const isOnline = (userId) => presence.find(p => p.user_id === userId)?.online || false;
  const onlineCount = presence.filter(p => p.online).length;

  const handleAnnounce = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(announceForm),
      });
      if (res.ok) {
        toast.success('Communiqué envoyé à tous les utilisateurs');
        setIsAnnounceOpen(false);
        setAnnounceForm({ title: '', message: '' });
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };
  const [isNewConvoOpen, setIsNewConvoOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [showMobileList, setShowMobileList] = useState(true);
  const [convoSearch, setConvoSearch] = useState('');
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const pollRef = useRef(null);
  const inputRef = useRef(null);

  // --- DATA FETCHING ---
  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/messages/conversations`, { credentials: 'include' });
      if (res.ok) setConversations(await res.json());
    } catch (e) { logError('Fetch convos:', e); }
  }, []);

  const fetchMessages = useCallback(async (convoId) => {
    try {
      const res = await fetch(`${API}/api/messages/${convoId}`, { credentials: 'include' });
      if (res.ok) {
        setMessages(await res.json());
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (e) { logError('Fetch messages:', e); }
  }, []);

  useEffect(() => {
    const init = async () => {
      const res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
      if (res.ok) setCurrentUser(await res.json());
      await fetchConversations();
      const uRes = await fetch(`${API}/api/messages/users/all`, { credentials: 'include' });
      if (uRes.ok) setAllUsers(await uRes.json());
    };
    init();
  }, [fetchConversations]);

  useEffect(() => {
    if (activeConvo) {
      fetchMessages(activeConvo);
      pollRef.current = setInterval(() => {
        fetchMessages(activeConvo);
        fetchConversations();
      }, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConvo, fetchMessages, fetchConversations]);

  // --- ACTIONS ---
  const selectConversation = (convoId) => {
    setActiveConvo(convoId);
    setShowMobileList(false);
    setAttachments([]);
    setRecording(false);
  };

  const startNewConvo = async (recipientId) => {
    try {
      const res = await fetch(`${API}/api/messages/conversations`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_id: recipientId }),
      });
      if (res.ok) {
        const convo = await res.json();
        setIsNewConvoOpen(false);
        await fetchConversations();
        selectConversation(convo.conversation_id);
      }
    } catch { toast.error('Erreur de création'); }
  };

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    if (!activeConvo || (!newMessage.trim() && attachments.length === 0)) return;
    try {
      const res = await fetch(`${API}/api/messages/${activeConvo}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: newMessage.trim(), attachments }),
      });
      if (res.ok) {
        setNewMessage('');
        setAttachments([]);
        fetchMessages(activeConvo);
        fetchConversations();
        inputRef.current?.focus();
      }
    } catch { toast.error('Erreur d\'envoi'); }
  };

  // --- FILE UPLOAD ---
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/api/messages/upload`, { method: 'POST', credentials: 'include', body: fd });
    if (!res.ok) throw new Error('Upload failed');
    return await res.json();
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let count = 0;
    for (const file of files) {
      try {
        const result = await uploadFile(file);
        setAttachments(prev => [...prev, result]);
        count++;
      } catch { toast.error(`Échec: ${file.name}`); }
    }
    if (count > 0) toast.success(`${count} fichier(s) prêt(s)`);
    setUploading(false);
    e.target.value = '';
  };

  // --- PHOTO CAPTURE ---
  const handleCapture = async (type) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        type === 'photo' ? { video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } }
          : { video: { facingMode: 'environment' }, audio: true }
      );

      if (type === 'photo') {
        const video = document.createElement('video');
        video.srcObject = stream;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('autoplay', 'true');
        await new Promise(r => { video.onloadedmetadata = r; });
        await video.play();
        await new Promise(r => setTimeout(r, 600));

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        canvas.getContext('2d').drawImage(video, 0, 0);
        stream.getTracks().forEach(t => t.stop());

        const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.85));
        if (!blob) { toast.error('Capture échouée'); return; }

        setUploading(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, `photo_${Date.now()}.jpg`);
          const result = await uploadFile(fd.get('file'));
          setAttachments(prev => [...prev, result]);
          toast.success('Photo capturée');
        } catch { toast.error('Erreur upload photo'); }
        setUploading(false);
      } else {
        // Video recording
        startMediaRecording(stream, 'video');
      }
    } catch (err) {
      logError('Capture error:', err);
      toast.error('Accès caméra refusé. Vérifiez les permissions.');
    }
  };

  // --- AUDIO / VIDEO RECORDING ---
  const startMediaRecording = (stream, type) => {
    setRecording(true);

    const mimeOptions = type === 'audio'
      ? ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
      : ['video/webm;codecs=vp9,opus', 'video/webm', 'video/mp4'];

    let selectedMime = '';
    for (const mime of mimeOptions) {
      if (MediaRecorder.isTypeSupported(mime)) { selectedMime = mime; break; }
    }

    const recorder = new MediaRecorder(stream, selectedMime ? { mimeType: selectedMime } : {});
    mediaRecorderRef.current = { recorder, stream };
    chunksRef.current = [];

    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.start(500);
  };

  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      startMediaRecording(stream, 'audio');
    } catch {
      toast.error('Accès micro refusé. Vérifiez les permissions.');
    }
  };

  const stopAndSendRecording = async () => {
    if (!mediaRecorderRef.current) return;
    const { recorder, stream } = mediaRecorderRef.current;

    return new Promise((resolve) => {
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setRecording(false);

        if (chunksRef.current.length === 0) { resolve(); return; }

        const mime = recorder.mimeType || 'audio/webm';
        const ext = mime.includes('mp4') ? 'mp4' : mime.includes('ogg') ? 'ogg' : 'webm';
        const isAudio = mime.startsWith('audio');
        const blob = new Blob(chunksRef.current, { type: mime });

        setUploading(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, `${isAudio ? 'audio' : 'video'}_${Date.now()}.${ext}`);
          const result = await uploadFile(fd.get('file'));
          setAttachments(prev => [...prev, result]);
          toast.success(isAudio ? 'Audio prêt' : 'Vidéo prête');
        } catch { toast.error('Erreur enregistrement'); }
        setUploading(false);
        resolve();
      };
      recorder.stop();
    });
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current) {
      const { recorder, stream } = mediaRecorderRef.current;
      recorder.stop();
      stream.getTracks().forEach(t => t.stop());
    }
    chunksRef.current = [];
    setRecording(false);
  };

  // --- HELPERS ---
  const getConvoName = (convo) => {
    if (convo.is_group) return convo.group_name || 'Groupe';
    const other = convo.participants_info?.find(p => p.user_id !== currentUser?.user_id);
    return other?.name || 'Conversation';
  };
  const getConvoAvatar = (convo) => {
    if (convo.is_group) return null;
    return convo.participants_info?.find(p => p.user_id !== currentUser?.user_id)?.picture;
  };

  const filteredUsers = allUsers.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeConvoData = conversations.find(c => c.conversation_id === activeConvo);

  return (
    <div className="h-[calc(100vh-7rem)] flex bg-[#020617] rounded-lg overflow-hidden border border-[#1E293B]" data-testid="messages-page">
      {/* ===== LEFT: CONVERSATION LIST ===== */}
      <div className={`${showMobileList ? 'flex' : 'hidden'} lg:flex flex-col w-full lg:w-80 border-r border-[#1E293B] bg-[#0F172A]`}>
        <div className="h-14 px-4 border-b border-[#1E293B] flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-[#F8FAFC]">Messages</h2>
            <p className="text-[11px] text-[#10B981] flex items-center gap-1" data-testid="online-count">
              <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] inline-block animate-pulse" /> {onlineCount} en ligne
            </p>
          </div>
          <div className="flex items-center gap-1">
            {['admin', 'drh', 'supervisor'].includes(currentUser?.role) && (
              <Button size="sm" onClick={() => setIsAnnounceOpen(true)} className="bg-[#F59E0B] hover:bg-[#D97706] text-white h-8 w-8 p-0" title="Envoyer un communiqué à tous" data-testid="announce-btn">
                <Megaphone size={16} />
              </Button>
            )}
            <Button size="sm" onClick={() => setIsNewConvoOpen(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-8 w-8 p-0" data-testid="new-convo-btn">
              <Plus size={16} />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="p-2 border-b border-[#1E293B]/50">
            <input
              value={convoSearch}
              onChange={(e) => setConvoSearch(e.target.value)}
              placeholder="Rechercher une conversation..."
              className="w-full bg-[#1E293B] border border-[#334155] rounded-md px-3 py-2 text-sm text-[#F8FAFC] placeholder-[#64748B] focus:outline-none focus:border-[#2563EB]"
              data-testid="search-conversation"
            />
          </div>
          {(() => {
            const filteredConvos = conversations.filter(c => !convoSearch || getConvoName(c).toLowerCase().includes(convoSearch.toLowerCase()));
            return filteredConvos.length === 0 ? (
            <div className="p-8 text-center text-[#94A3B8] text-sm">Aucune conversation</div>
          ) : filteredConvos.map(convo => (
            <button
              key={convo.conversation_id}
              onClick={() => selectConversation(convo.conversation_id)}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#1E293B]/50 transition-colors text-left border-b border-[#1E293B]/30 ${activeConvo === convo.conversation_id ? 'bg-[#1E293B]' : ''}`}
              data-testid={`convo-${convo.conversation_id}`}
            >
              {convo.is_group ? (
                <div className="h-11 w-11 rounded-full bg-[#10B981] flex items-center justify-center text-white flex-shrink-0">
                  <UsersThree size={22} weight="fill" />
                </div>
              ) : (
                <div className="relative flex-shrink-0">
                  {getConvoAvatar(convo) ? (
                    <img src={getConvoAvatar(convo)} alt="" className="h-11 w-11 rounded-full object-cover" />
                  ) : (
                    <div className="h-11 w-11 rounded-full bg-[#2563EB] flex items-center justify-center text-white font-semibold">
                      {getConvoName(convo).charAt(0)}
                    </div>
                  )}
                  {isOnline(convo.participants_info?.find(p => p.user_id !== currentUser?.user_id)?.user_id) && (
                    <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-[#10B981] border-2 border-[#0F172A]" data-testid="online-dot" />
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium text-[#F8FAFC] truncate ${convo.is_group ? 'font-bold' : ''}`}>{getConvoName(convo)}</span>
                  {convo.unread_count > 0 && (
                    <Badge className="bg-[#2563EB] text-white text-[10px] h-5 min-w-[20px] flex items-center justify-center px-1.5 rounded-full ml-2">{convo.unread_count}</Badge>
                  )}
                </div>
                <p className="text-xs text-[#94A3B8] truncate mt-0.5">{convo.last_message_preview || 'Nouvelle conversation'}</p>
              </div>
            </button>
          ));
          })()}
        </div>
      </div>

      {/* ===== RIGHT: CHAT AREA ===== */}
      <div className={`${!showMobileList ? 'flex' : 'hidden'} lg:flex flex-col flex-1 bg-[#0B1120]`}>
        {activeConvo && activeConvoData ? (
          <>
            {/* Header */}
            <div className="h-14 px-4 border-b border-[#1E293B] flex items-center gap-3 bg-[#0F172A] flex-shrink-0">
              <button onClick={() => setShowMobileList(true)} className="lg:hidden text-[#94A3B8] hover:text-white mr-1">
                <ArrowLeft size={20} />
              </button>
              {activeConvoData.is_group ? (
                <div className="h-9 w-9 rounded-full bg-[#10B981] flex items-center justify-center text-white"><UsersThree size={18} weight="fill" /></div>
              ) : getConvoAvatar(activeConvoData) ? (
                <img src={getConvoAvatar(activeConvoData)} alt="" className="h-9 w-9 rounded-full object-cover" />
              ) : (
                <div className="h-9 w-9 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-semibold">{getConvoName(activeConvoData).charAt(0)}</div>
              )}
              <div>
                <p className="text-sm font-semibold text-[#F8FAFC]">{getConvoName(activeConvoData)}</p>
                {activeConvoData.is_group && <p className="text-[11px] text-[#94A3B8]">{activeConvoData.participants?.length || 0} membres</p>}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {messages.map(msg => (
                <MessageBubble key={msg.message_id} msg={msg} isMine={msg.sender_id === currentUser?.user_id} />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Attachment preview */}
            <AttachmentPreview attachments={attachments} onRemove={(i) => setAttachments(prev => prev.filter((_, idx) => idx !== i))} />

            {/* Input bar */}
            <div className="border-t border-[#1E293B] bg-[#0F172A] flex-shrink-0">
              {recording ? (
                <AudioRecorder onSend={async () => { await stopAndSendRecording(); }} onCancel={cancelRecording} />
              ) : (
                <form onSubmit={sendMessage} className="flex items-center gap-2 px-3 py-2.5">
                  {/* Attach button */}
                  <div className="relative">
                    <button type="button" onClick={() => setAttachMenuOpen(!attachMenuOpen)} className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${attachMenuOpen ? 'bg-[#2563EB] text-white' : 'text-[#94A3B8] hover:text-white hover:bg-[#1E293B]'}`} title="Joindre" data-testid="attach-btn">
                      <Paperclip size={20} />
                    </button>
                    <AttachMenu open={attachMenuOpen} onClose={() => setAttachMenuOpen(false)} onSelectFile={handleFileUpload} onCapture={handleCapture} />
                  </div>

                  {/* Text input */}
                  <Input
                    ref={inputRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écrire un message..."
                    className="flex-1 bg-[#1E293B] border-[#334155] text-[#F8FAFC] rounded-full text-sm h-10"
                    disabled={uploading}
                    data-testid="message-input"
                  />

                  {/* Audio or Send */}
                  {!newMessage.trim() && attachments.length === 0 ? (
                    <button type="button" onClick={startAudioRecording} className="h-10 w-10 rounded-full bg-[#1E293B] hover:bg-[#334155] flex items-center justify-center text-[#94A3B8] hover:text-white transition-colors" title="Enregistrer un vocal" data-testid="record-audio-btn">
                      <Microphone size={20} />
                    </button>
                  ) : (
                    <Button type="submit" size="icon" disabled={uploading} className="h-10 w-10 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="send-message-btn">
                      <PaperPlaneRight size={18} />
                    </Button>
                  )}
                </form>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-[#94A3B8] gap-4">
            <div className="h-20 w-20 rounded-full bg-[#1E293B] flex items-center justify-center">
              <PaperPlaneRight size={36} className="opacity-40" />
            </div>
            <div className="text-center">
              <p className="font-medium text-[#F8FAFC]">Vos messages</p>
              <p className="text-sm mt-1">Sélectionnez une conversation ou créez-en une nouvelle</p>
            </div>
          </div>
        )}
      </div>

      {/* ===== NEW CONVERSATION DIALOG ===== */}
      <Dialog open={isNewConvoOpen} onOpenChange={setIsNewConvoOpen}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold">Nouvelle conversation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Rechercher..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] pl-9" />
            </div>
            <div className="max-h-[300px] overflow-y-auto space-y-0.5">
              {filteredUsers.map(u => (
                <button key={u.user_id} onClick={() => startNewConvo(u.user_id)} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#1E293B] transition-colors text-left">
                  {u.picture ? <img src={u.picture} alt="" className="h-10 w-10 rounded-full object-cover" /> : (
                    <div className="h-10 w-10 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-semibold">{u.name?.charAt(0) || '?'}</div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-[#F8FAFC]">{u.name}</p>
                    <p className="text-xs text-[#94A3B8] capitalize">{u.role}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Communiqué */}
      <Dialog open={isAnnounceOpen} onOpenChange={setIsAnnounceOpen}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold flex items-center gap-2"><Megaphone size={20} className="text-[#F59E0B]" /> Communiqué général</DialogTitle></DialogHeader>
          <p className="text-sm text-[#94A3B8]">Ce communiqué sera envoyé en notification à tous les utilisateurs de l'application.</p>
          <form onSubmit={handleAnnounce} className="space-y-4">
            <input
              value={announceForm.title}
              onChange={e => setAnnounceForm({ ...announceForm, title: e.target.value })}
              required placeholder="Titre du communiqué"
              className="w-full bg-[#1E293B] border border-[#334155] rounded-md px-3 py-2.5 text-sm text-[#F8FAFC] placeholder-[#64748B] focus:outline-none focus:border-[#F59E0B]"
              data-testid="announce-title"
            />
            <textarea
              value={announceForm.message}
              onChange={e => setAnnounceForm({ ...announceForm, message: e.target.value })}
              required rows={4} placeholder="Contenu du communiqué..."
              className="w-full bg-[#1E293B] border border-[#334155] rounded-md px-3 py-2.5 text-sm text-[#F8FAFC] placeholder-[#64748B] focus:outline-none focus:border-[#F59E0B]"
              data-testid="announce-message"
            />
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsAnnounceOpen(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
              <Button type="submit" className="bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="announce-send-button">
                <Megaphone size={15} className="mr-2" /> Diffuser
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
